import { getIn, useFormikContext } from 'formik'
import type { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { JSX, useCallback, useMemo, useRef } from 'react'

import { BaseFieldVariant, LabelConstraint } from '@ui/components/Input/BaseField/BaseField'
import { Input } from '@ui/components/Input/Input'
import { IconFCType } from '@ui/icons/types'

import { removeRestrictedSpecialCharacters } from '@utils/validations'

import { Box } from '@shared/components/Box'
import { useLoadingDots } from '@shared/components/Loading/Loading'

import { DeepNonNullable } from '../types/DeepNonNullable'
import { DeepRequired } from '../types/DeepRequired'
import type { TypedFieldProps } from '../types/TypedFieldProps'
import { ValidateField } from '../types/ValidateField'
import { getError, getFieldValid, getLabel, getName, getPlaceholder, getValue } from './helpers'

type FieldConfig = Omit<FormikFieldConfig, 'name' | 'label' | 'placeholder'>

interface CommonFormFieldProps extends FieldConfig {
  namePrefix?: string
  hint?: string | React.ReactNode
  tooltip?: string
  onChange?(value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
  onKeyDown?({ key, preventDefault }: { key: string; preventDefault: () => any }): void
  onClick?: () => void
  onDisabledClick?: (event: React.MouseEvent<HTMLElement>) => void
  validateOnType?: boolean
  hidePlaceholderAsterisk?: boolean
  loading?: boolean
  variant?: BaseFieldVariant
  autoComplete?: string
  rightIcon?: IconFCType
  leftIcon?: IconFCType
  renderRightIconJSX?: React.ReactNode
}

type FormFieldProps = CommonFormFieldProps & LabelConstraint

export function RawFormField<FormValues>(
  props: FormFieldProps & Omit<TypedFieldProps<FormValues>, 'placeholder' | 'label'>
): React.JSX.Element {
  const {
    namePrefix = '',
    name = '',
    hint,
    loading,
    type = 'text',
    required,
    validateOnType,
    hidePlaceholderAsterisk,
    onKeyDown,
    onClick,
    rightIcon,
    leftIcon,
    fullWidth,
    disabled,
    renderRightIconJSX,
    onChange,
  } = props

  const loadingDots = useLoadingDots(!!loading)

  const fieldName = getName(namePrefix, props)
  const fieldRef = useRef<HTMLDivElement>(null)
  const context = useFormikContext<FormValues>()

  const rawValue = getValue(context.values, props.value, name)

  const value = typeof rawValue === 'string' || typeof rawValue === 'number' ? rawValue : ''

  const error = getError(context.errors, props.error, name)
  const touched = getIn(context.touched, name)

  const label = getLabel(hidePlaceholderAsterisk ? { ...props, required: false } : props)
  const placeholder = getPlaceholder(
    hidePlaceholderAsterisk ? { ...props, required: false } : props
  )

  const labelProps = useMemo(
    () => (label ? { label, placeholder } : { label: undefined, placeholder: undefined }),
    [label, placeholder]
  )

  const isValid = getFieldValid(error, touched, !!validateOnType && !!rawValue)

  const handleClick = useCallback(() => {
    if (!disabled && !loading && onClick) {
      onClick()
    }
  }, [disabled, loading, onClick])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = removeRestrictedSpecialCharacters(event.target.value)

      if (onChange) {
        onChange(event)
      } else {
        context.setFieldValue(fieldName, newValue)
      }

      if (validateOnType) {
        context.validateField(fieldName)
      }
    },
    [onChange, context, fieldName, validateOnType]
  )

  const handleBlur = useCallback(() => {
    context.setFieldTouched(fieldName, true)
  }, [context, fieldName])

  const showHint = useMemo(
    () => (touched || validateOnType ? error : hint),
    [touched, validateOnType, error, hint]
  )

  return (
    <Box cursor={disabled ? 'not-allowed' : 'pointer'} fullWidth={fullWidth} ref={fieldRef}>
      <Input
        name={fieldName}
        {...labelProps}
        value={loading ? loadingDots : value}
        type={type}
        required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!isValid}
        hint={showHint}
        autoComplete={props.autoComplete}
        variant={props.variant || 'default'}
        disabled={props.disabled}
        readOnly={props.readOnly}
        onKeyDown={onKeyDown}
        rightIcon={rightIcon}
        leftIcon={leftIcon}
        renderRightIconJSX={renderRightIconJSX}
        boxRef={fieldRef}
        fullWidth={fullWidth}
        onClick={handleClick}
      />
    </Box>
  )
}

export function createFormField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ): JSX.Element {
    return <RawFormField {...props} />
  }
}
